'use client';

import { c as _c } from "react/compiler-runtime";
import { Icon } from '~/components/primitives';
import { useI18n } from '~/features/i18n';
import { addressCityLabel } from '~/features/activities/addressCityLabel';
import { useCustomLocationLabel } from '~/features/activities/useCustomLocationLabel';
import type { CartItemType } from './CartItemCard';

/**
 * The location chosen for a cart item, rendered wherever a quote is summarised (the
 * checkout sidebar on desktop, the collapsible summary on mobile). Renders nothing when
 * no location has been picked — that is a valid state, since a location can also be
 * chosen later in checkout.
 */
export const CartItemLocationLine: React.FC<{
  item: Pick<CartItemType, 'locationType' | 'address'>;
  className?: string;
}> = props => {
  const $ = _c(9);
  const i18n = useI18n();
  const customLocationLabel = useCustomLocationLabel();
  if (props.item.locationType == null) {
    return null;
  }
  const t0 = props.className ?? "flex items-center gap-1.5 text-sm text-blue-600";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon name="location" size="xs" className="shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== customLocationLabel || $[2] !== i18n || $[3] !== props.item.address?.name || $[4] !== props.item.locationType) {
    t2 = props.item.locationType === "CUSTOM" ? customLocationLabel : addressCityLabel(props.item.address?.name) ?? i18n._("pdp.location.dropdown.label", undefined, {
      message: "Location"
    });
    $[1] = customLocationLabel;
    $[2] = i18n;
    $[3] = props.item.address?.name;
    $[4] = props.item.locationType;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t2) {
    t3 = <p data-testid="cart-item-location" className={t0}>{t1}{t2}</p>;
    $[6] = t0;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};